import { useAtomValue } from "jotai";
import { useTranslation } from "react-i18next";

import { InformationIcon } from "~/components/ui/icons/information";
import {
  Tooltip,
  TooltipArrow,
  TooltipContent,
  TooltipTrigger,
} from "~/components/ui/tooltip";
import {
  type BillingByVatV2,
  SupplierBillingAdjustmentChargeTo,
} from "~/graphql/graphql-new.gen";
import { activeSaleState } from "~/stores/sales";
import { formatPrice } from "~/utils/currency";
import { formatPercentage } from "~/utils/number";
import { hasBillingErrors } from "~/utils/sales";

import { AdminWithTooltip } from "./admin-with-tooltip";
import { BillingProductSummary } from "./billing-product-summary";
import { DashedLine } from "./dashed-line";
import { useBilling } from "./use-billing";

type BillingSummaryProps = {
  isInternalUser: boolean;
  isSubjectVat: boolean;
};

export const BillingSummary: React.FC<BillingSummaryProps> = ({
  isInternalUser,
  isSubjectVat,
}: BillingSummaryProps) => {
  const { data: billing, error: billingError } = useBilling();
  const { i18n, t } = useTranslation();

  const activeSale = useAtomValue(activeSaleState);

  const locale = i18n.language === "fr" ? "fr" : "en";

  const hasBillingError = hasBillingErrors(billingError, billing);

  if (!billing || (hasBillingError && !(billing.has_error && isInternalUser))) {
    return null;
  }

  const validatedAdjustments = billing.adjustments.filter(
    (adjustment) =>
      adjustment.__typename === "SupplierBillingAdjustmentValidated",
  );

  return (
    <div className="relative overflow-hidden rounded-lg border border-gray-100">
      <div className="space-y-8 py-6">
        {billing.agg_by_vat_rate
          .filter((vat_rate): vat_rate is BillingByVatV2 => !!vat_rate)
          .map((billingByVat) => (
            <div key={billingByVat.vat_rate}>
              <div className="px-6 pb-2 text-xs font-semibold uppercase tracking-wider text-gray-500">
                {t("invoice.products")}{" "}
                {billingByVat.vat_rate !== "0" ? (
                  <>
                    {t("invoice.tva")} (
                    {formatPercentage(
                      parseFloat(billingByVat.vat_rate) / 100,
                      locale,
                    )}
                    )
                  </>
                ) : null}
              </div>

              <BillingProductSummary
                product_stats={billingByVat}
                isInternalUser={isInternalUser}
                isSubjectVat={isSubjectVat}
                locale={locale}
              />
            </div>
          ))}
        <div>
          <div className="px-6 pb-2 text-xs font-semibold uppercase tracking-wider text-gray-500">
            {t("invoice.delivery")}{" "}
            {isSubjectVat ? formatPercentage(20 / 100, locale) : null}
          </div>
          <div className="flex justify-between space-x-2 px-6 py-2 text-base leading-5.5 text-gray-700 transition-colors duration-300 hover:bg-gray-50">
            <div className="flex shrink-0 items-center">
              <span>{t("invoice.amount_billing_ht")}</span>
            </div>
            <div className="flex w-full items-end pb-1.5">
              <DashedLine />
            </div>
            <div className="shrink-0">
              {formatPrice(
                billing.total_delivery_ht / 100,
                locale,
                activeSale?.currency.toString().toUpperCase() ?? "EUR",
              )}
            </div>
          </div>
          {isSubjectVat ? (
            <div className="flex justify-between space-x-2 px-6 py-2 text-base leading-5.5 text-gray-700 transition-colors duration-300 hover:bg-gray-50">
              <div className="flex shrink-0 items-center">
                <span>
                  {t("invoice.delivery_vat")} (
                  {formatPercentage(20 / 100, locale)})
                </span>
              </div>
              <div className="flex w-full items-end pb-1.5">
                <DashedLine />
              </div>
              <div className="shrink-0">
                {formatPrice(
                  (billing.total_delivery_ttc - billing.total_delivery_ht) /
                    100,
                  locale,
                  activeSale?.currency.toString().toUpperCase() ?? "EUR",
                )}
              </div>
            </div>
          ) : null}
          {isSubjectVat ? (
            <div className="flex justify-between space-x-2 px-6 py-2 text-base leading-5.5 text-gray-700 transition-colors duration-300 hover:bg-gray-50">
              <div className="flex shrink-0 items-center">
                <span>{t("invoice.amount_billing_ttc")}</span>
              </div>
              <div className="flex w-full items-end pb-1.5">
                <DashedLine />
              </div>
              <div className="shrink-0">
                {formatPrice(
                  billing.total_delivery_ttc / 100,
                  locale,
                  activeSale?.currency.toString().toUpperCase() ?? "EUR",
                )}
              </div>
            </div>
          ) : null}
        </div>

        {validatedAdjustments.length > 0
          ? validatedAdjustments.map((adjustment) => (
              <div key={adjustment.supplierBillingAdjustmentId}>
                <div className="flex items-center gap-1 truncate px-6 pb-2 text-gray-500">
                  <span className="truncate text-xs font-semibold uppercase tracking-wider">
                    {adjustment.title}
                  </span>

                  <Tooltip>
                    <TooltipTrigger>
                      <InformationIcon className="size-4" />
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>{t("invoice.financial_adjustment_tooltip")}</p>
                      <TooltipArrow />
                    </TooltipContent>
                  </Tooltip>
                </div>

                <div>
                  <div className="flex justify-between space-x-2 px-6 py-2 text-base leading-5.5 text-gray-700 transition-colors duration-300 hover:bg-gray-50">
                    <div className="flex shrink-0 items-center space-x-2">
                      <span>{t("invoice.amount_billing_ht")}</span>
                    </div>
                    <div className="flex w-full items-end pb-1.5">
                      <DashedLine />
                    </div>
                    <div className="shrink-0">
                      {formatPrice(
                        ((adjustment.chargedTo ===
                        SupplierBillingAdjustmentChargeTo.Supplier
                          ? -1
                          : 1) *
                          adjustment.amountVatExcludedInCents) /
                          100,
                        locale,
                        activeSale?.currency.toString().toUpperCase() ?? "EUR",
                      )}
                    </div>
                  </div>
                </div>
                {isSubjectVat ? (
                  <div>
                    <div className="flex justify-between space-x-2 px-6 py-2 text-base leading-5.5 text-gray-700 transition-colors duration-300 hover:bg-gray-50">
                      <div className="flex shrink-0 items-center space-x-2">
                        <span>
                          {t("invoice.tva")} (
                          {formatPercentage(adjustment.vatRate / 100, locale)})
                        </span>
                      </div>
                      <div className="flex w-full items-end pb-1.5">
                        <DashedLine />
                      </div>
                      <div className="shrink-0">
                        {formatPrice(
                          ((adjustment.chargedTo ===
                          SupplierBillingAdjustmentChargeTo.Supplier
                            ? -1
                            : 1) *
                            (adjustment.amountVatIncludedInCents -
                              adjustment.amountVatExcludedInCents)) /
                            100,
                          locale,
                          activeSale?.currency.toString().toUpperCase() ??
                            "EUR",
                        )}
                      </div>
                    </div>
                  </div>
                ) : null}
                {isSubjectVat ? (
                  <div>
                    <div className="flex justify-between space-x-2 px-6 py-2 text-base leading-5.5 text-gray-700 transition-colors duration-300 hover:bg-gray-50">
                      <div className="flex shrink-0 items-center space-x-2">
                        <span>{t("invoice.amount_billing_ttc")}</span>
                      </div>
                      <div className="flex w-full items-end pb-1.5">
                        <DashedLine />
                      </div>
                      <div className="shrink-0">
                        {formatPrice(
                          ((adjustment.chargedTo ===
                          SupplierBillingAdjustmentChargeTo.Supplier
                            ? -1
                            : 1) *
                            adjustment.amountVatIncludedInCents) /
                            100,
                          locale,
                          activeSale?.currency.toString().toUpperCase() ??
                            "EUR",
                        )}
                      </div>
                    </div>
                  </div>
                ) : null}
              </div>
            ))
          : null}
      </div>
      <div className="space-y-2 border-t border-gray-100 bg-[#FBFBFB] py-6">
        {isInternalUser ? (
          <div className="flex justify-between space-x-2 px-6 text-gray-700">
            <div className="flex items-center space-x-2">
              <span>{t("invoice.total_operation_ht")}</span>
              <AdminWithTooltip />
            </div>
            <div>
              {formatPrice(
                billing.total_operation_ht / 100,
                locale,
                activeSale?.currency.toString().toUpperCase() ?? "EUR",
              )}
            </div>
          </div>
        ) : null}
        {isInternalUser ? (
          <div className="flex justify-between space-x-2 px-6 text-gray-700">
            <div className="flex items-center space-x-2">
              <span>{t("invoice.total_refunded_ht")}</span>
              <AdminWithTooltip />
            </div>
            <div>
              {formatPrice(
                -billing.total_refund_ht / 100,
                locale,
                activeSale?.currency.toString().toUpperCase() ?? "EUR",
              )}
            </div>
          </div>
        ) : null}
        {isInternalUser ? (
          <div className="flex justify-between space-x-2 px-6 text-gray-700">
            <div className="flex items-center space-x-2">
              <span>{t("invoice.total_choose_margin_ht")}</span>
              <AdminWithTooltip />
            </div>
            <div>
              {formatPrice(
                -billing.total_commission_ht / 100,
                locale,
                activeSale?.currency.toString().toUpperCase() ?? "EUR",
              )}
            </div>
          </div>
        ) : null}
        {isInternalUser ? (
          <div className="flex justify-between space-x-2 px-6 text-gray-700">
            <div className="flex items-center space-x-2">
              <span>{t("invoice.total_adjustment_ht")}</span>
              <AdminWithTooltip />
            </div>
            <div>
              {formatPrice(
                billing.total_adjustment_ht / 100,
                locale,
                activeSale?.currency.toString().toUpperCase() ?? "EUR",
              )}
            </div>
          </div>
        ) : null}
        {isInternalUser && billing.total_eee_price_ht > 0 && isSubjectVat ? (
          <div className="flex justify-between space-x-2 px-6 text-gray-700">
            <div className="flex items-center space-x-2">
              <span>{t("invoice.total_eee_price_ht")}</span>
              <AdminWithTooltip />
            </div>
            <div>
              {formatPrice(
                billing.total_eee_price_ht / 100,
                locale,
                activeSale?.currency.toString().toUpperCase() ?? "EUR",
              )}
            </div>
          </div>
        ) : null}

        {isSubjectVat ? (
          <div className="flex justify-between space-x-2 px-6 font-semibold text-gray-900">
            <div>
              {billing.total_eee_price_ht
                ? t("invoice.total_billing_ht_with_eee")
                : t("invoice.total_billing_ht")}
            </div>
            <div>
              {formatPrice(
                billing.total_billing_ht / 100,
                locale,
                activeSale?.currency.toString().toUpperCase() ?? "EUR",
              )}
            </div>
          </div>
        ) : null}

        {isSubjectVat ? (
          <div className="flex justify-between space-x-2 px-6 text-gray-700">
            <div>{t("invoice.total_vat")}</div>
            <div>
              {formatPrice(
                (billing.total_billing_ttc - billing.total_billing_ht) / 100,
                locale,
                activeSale?.currency.toString().toUpperCase() ?? "EUR",
              )}
            </div>
          </div>
        ) : null}

        <div className="flex justify-between space-x-2 px-6 text-2xl font-bold text-gray-900">
          <div>
            {isSubjectVat ? t("invoice.total_ttc") : t("invoice.total")}
          </div>
          <div className="shrink-0">
            {formatPrice(
              (isSubjectVat
                ? billing.total_billing_ttc
                : billing.total_billing_ht) / 100,
              locale,
              activeSale?.currency.toString().toUpperCase() ?? "EUR",
            )}
          </div>
        </div>
        {billing.total_eee_price_ttc > 0 ? (
          <div className="flex justify-between space-x-2 px-6 text-gray-700">
            <div>{t("invoice.total_eee_price_ttc")}</div>
            <div>
              {formatPrice(
                billing.total_eee_price_ttc / 100,
                locale,
                activeSale?.currency.toString().toUpperCase() ?? "EUR",
              )}
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
};
